import { DateFieldUI } from '@axo/form/feature/form-input/DateField/DateFieldUI';
import { DropdownFieldUI } from '@axo/form/feature/form-input/DropdownField/DropdownFieldUI';
import { NumberFieldUI } from '@axo/form/feature/form-input/NumberField/NumberFieldUI';
import { TextFieldUI } from '@axo/form/feature/form-input/TextField/TextFieldUI';
import { EmploymentStatus, MaritalStatus } from '@axo/shared/data-access/types';
import { useTranslation } from '@axo/shared/i18n';
import { Grid } from '@axo/ui-core/components/layout/item';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { formatSpacedNumber } from '../../../../../../../../utils/format';
import { CoApplicantEmploymentData } from '../../../../../FormData.no.types';
import { employmentOptions } from '../../../../../config/globals';
import { gridColumns } from '../../../../../config/layouts/grid';
import {
  coApplicantEmployerFieldOption,
  coApplicantEmploymentStatusFieldOption,
  coApplicantEmploymentStatusSinceFieldOption,
  coApplicantGrossIncomeYearlyFieldOption,
} from './config';

export const CoApplicantEmployment = () => {
  const { t } = useTranslation();

  const { watch } = useFormContext();

  const currentYear = new Date().getFullYear();

  const isCohabitant = watch('LoanApplication/CoApplicantIsCohabitant');

  const employmentStatus = watch('LoanApplication/CoApplicantEmploymentStatus');

  const mainApplicantMaritalStatus = watch('LoanApplication/MaritalStatus');

  const isCoApplicantGrossIncomeVisible =
    isCohabitant === 'No' ||
    (isCohabitant === 'Yes' &&
      mainApplicantMaritalStatus === MaritalStatus.Single);

  const askEmploymentDetails = useMemo(
    () =>
      [
        EmploymentStatus.Entrepreneur,
        EmploymentStatus.Temporary,
        EmploymentStatus.Permanent,
        EmploymentStatus.PermanentPublic,
      ].includes(employmentStatus),
    [employmentStatus]
  );

  return (
    <Grid columns={gridColumns}>
      <DropdownFieldUI<CoApplicantEmploymentData>
        name="LoanApplication/CoApplicantEmploymentStatus"
        label={t('EmploymentStatus.label', { context: 'coapp' })}
        data-testid="employment-dropdown"
        options={employmentOptions}
        {...coApplicantEmploymentStatusFieldOption}
      />

      {isCoApplicantGrossIncomeVisible ? (
        <NumberFieldUI<CoApplicantEmploymentData>
          name="LoanApplication/CoApplicantGrossIncomeYearly"
          label={t('CoApplicantGrossIncomeYearly.label')}
          data-testid="gross-income-input"
          format={formatSpacedNumber}
          suffix={t('suffix.currency')}
          {...coApplicantGrossIncomeYearlyFieldOption}
        />
      ) : (
        // Empty div for grid
        <div />
      )}

      {askEmploymentDetails && (
        <>
          <DateFieldUI<CoApplicantEmploymentData>
            name="LoanApplication/CoApplicantEmploymentStatusSince"
            label={t('EmploymentStatusSince.label', { context: 'coapp' })}
            data-testid="employment-status-since-dropdown"
            minYear={currentYear - 10}
            maxYear={currentYear}
            minYearSuffix={t('EmploymentStatusSince.year.suffix')}
            yearPlaceholder={t('EmploymentStatusSince.placeholder.year')}
            i18n="no-NO"
            showYearOnly
            latestYearAtTop
            disableDatesInFuture
            rules={coApplicantEmploymentStatusSinceFieldOption.registerOptions}
          />

          <TextFieldUI<CoApplicantEmploymentData>
            name="LoanApplication/CoApplicantEmployer"
            label={t('CoApplicantEmployer.label')}
            data-testid="employer-input"
            {...coApplicantEmployerFieldOption}
          />
        </>
      )}
    </Grid>
  );
};
